/**
 * Social graph REST client (T2.2 / EPIC-7). Talks to the user API Gateway
 * (same base + auth as services/user/api.ts): `Authorization: <accessToken>`,
 * no Bearer — the Cognito JWT authorizer.
 *
 *   GET    {base}/user/{id}/social      → { followers, following, isFollowing, isSelf }
 *   GET    {base}/user/{id}/public      → PublicProfile | null (404)
 *   POST   {base}/user/{id}/follow      → follow
 *   DELETE {base}/user/{id}/follow      → unfollow
 *   GET    {base}/user/{id}/followers   → { followers: id[], cursor? }
 *   GET    {base}/user/{id}/following   → { following: id[], cursor? }
 */

import { getTokensFromCache } from '../auth/utils/tokenCache';

const USER_API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_USER_ENDPOINT;

export type FollowState = 'following' | 'requested' | 'none';

export interface SocialSummary {
  followers: number;
  following: number;
  isFollowing: boolean;
  /** D-35: 'requested' = pending request to a closed account. */
  followState?: FollowState;
  /** Pending incoming follow requests — only meaningful when isSelf. */
  pendingRequests?: number;
  isSelf: boolean;
}

export interface PublicProfile {
  id: string;
  firstName: string | null;
  lastName: string | null;
  nickname: string | null;
  pictureUrl: string | null;
}

export interface IdPage {
  ids: string[];
  cursor?: string;
}

function base(): string {
  if (!USER_API_ENDPOINT) {
    throw new Error('User API endpoint not configured (REACT_APP_APIGATEWAY_USER_ENDPOINT)');
  }
  return USER_API_ENDPOINT;
}

function authHeaders(): Record<string, string> {
  const token = getTokensFromCache()?.accessToken;
  if (!token) throw new Error('User not authenticated');
  return { 'Content-Type': 'application/json', Authorization: token };
}

const userUrl = (userId: string, suffix: string) =>
  `${base()}/user/${encodeURIComponent(userId)}${suffix}`;

export async function getSocialSummary(userId: string): Promise<SocialSummary> {
  const response = await fetch(userUrl(userId, '/social'), { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load social summary (${response.status})`);
  return response.json();
}

export async function getPublicProfile(userId: string): Promise<PublicProfile | null> {
  const response = await fetch(userUrl(userId, '/public'), { headers: authHeaders() });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to load profile (${response.status})`);
  return response.json();
}

/** Public target → 'following'; closed target → 'requested' (D-35). */
export async function followUser(userId: string): Promise<FollowState> {
  const response = await fetch(userUrl(userId, '/follow'), { method: 'POST', headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to follow (${response.status})`);
  const data = await response.json().catch(() => ({}));
  return data.state === 'requested' ? 'requested' : 'following';
}

export async function unfollowUser(userId: string): Promise<void> {
  const response = await fetch(userUrl(userId, '/follow'), { method: 'DELETE', headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to unfollow (${response.status})`);
}

export async function getFollowers(userId: string, cursor?: string): Promise<IdPage> {
  const suffix = `/followers${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;
  const response = await fetch(userUrl(userId, suffix), { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load followers (${response.status})`);
  const data = await response.json();
  return { ids: data.followers ?? [], cursor: data.cursor };
}

export async function getFollowing(userId: string, cursor?: string): Promise<IdPage> {
  const suffix = `/following${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`;
  const response = await fetch(userUrl(userId, suffix), { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load following (${response.status})`);
  const data = await response.json();
  return { ids: data.following ?? [], cursor: data.cursor };
}

// ── connections page (T2.2c/D-35): profile-expanded lists + request actions ───

export interface ConnectionPage {
  profiles: PublicProfile[];
  cursor?: string;
}

const toProfiles = (data: any, idsKey: string): PublicProfile[] => {
  if (Array.isArray(data.profiles)) return data.profiles;
  return (data[idsKey] ?? []).map((id: string) => ({
    id, firstName: null, lastName: null, nickname: null, pictureUrl: null,
  }));
};

export async function getFollowersWithProfiles(userId: string, cursor?: string): Promise<ConnectionPage> {
  const suffix = `/followers?expand=profile${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const response = await fetch(userUrl(userId, suffix), { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load followers (${response.status})`);
  const data = await response.json();
  return { profiles: toProfiles(data, 'followers'), cursor: data.cursor };
}

export async function getFollowingWithProfiles(userId: string, cursor?: string): Promise<ConnectionPage> {
  const suffix = `/following?expand=profile${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const response = await fetch(userUrl(userId, suffix), { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load following (${response.status})`);
  const data = await response.json();
  return { profiles: toProfiles(data, 'following'), cursor: data.cursor };
}

/** Pending incoming requests for the SIGNED-IN user (self-only server-side). */
export async function getFollowRequests(userId: string, cursor?: string): Promise<ConnectionPage> {
  const suffix = `/follow-requests?expand=profile${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`;
  const response = await fetch(userUrl(userId, suffix), { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load follow requests (${response.status})`);
  const data = await response.json();
  return { profiles: toProfiles(data, 'requests'), cursor: data.cursor };
}

export async function acceptFollowRequest(requesterId: string): Promise<void> {
  const response = await fetch(userUrl(requesterId, '/follow-request/accept'), {
    method: 'POST', headers: authHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to accept request (${response.status})`);
}

export async function declineFollowRequest(requesterId: string): Promise<void> {
  const response = await fetch(userUrl(requesterId, '/follow-request'), {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to decline request (${response.status})`);
}

export async function removeFollower(followerId: string): Promise<void> {
  const response = await fetch(userUrl(followerId, '/follower'), {
    method: 'DELETE', headers: authHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to remove follower (${response.status})`);
}
